import React, { useEffect, useMemo, useState } from 'react';
import { fmtDate } from '../utils/dateUtils';

const OCC_COLS = [
  { key: 'rate_single', label: 'single' },
  { key: 'rate_double', label: 'double' },
  { key: 'rate_triple', label: 'triple' },
  { key: 'rate_quad', label: 'quadruple' },
  { key: 'rate_extra_adult', label: 'extra adult' },
  { key: 'rate_extra_child', label: 'extra child' }
];

const toLocalDateOnly = (dateObj) => {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftDateByDays = (isoDate, days) => {
  const dt = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return isoDate;
  dt.setDate(dt.getDate() + Number(days || 0));
  return toLocalDateOnly(dt);
};

const getCurrentMonday = () => {
  const dt = new Date();
  dt.setHours(0, 0, 0, 0);
  const day = dt.getDay();
  const mondayOffset = day === 0 ? -6 : (1 - day);
  dt.setDate(dt.getDate() + mondayOffset);
  return toLocalDateOnly(dt);
};

const getLockReason = (row) => {
  const rateDate = String(row?.rate_date || '');
  const stayStart = String(row?.stay_start_date || '');
  const stayEnd = String(row?.stay_end_date || '');
  if (stayStart && rateDate && rateDate < stayStart) return 'Before plan start';
  if (stayEnd && rateDate && rateDate > stayEnd) return 'After plan end';
  return 'Locked';
};

const formatRateLabel = (value) => {
  if (value == null || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return String(numeric);
};

function PmsRateMatrix({ token, selectedProperty, onOpenUpdateRate }) {
  const [rows, setRows] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('ALL');
  const [weekStart, setWeekStart] = useState('');
  const [weekEnd, setWeekEnd] = useState('');
  const [weekCursor, setWeekCursor] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [values, setValues] = useState({});

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const cellKey = (row, colKey) => `${row.rate_plan_id}|${row.rate_date}|${colKey}`;

  const loadRatePlans = async () => {
    if (!selectedProperty?.id) {
      setRatePlans([]);
      setSelectedPlanId('ALL');
      return;
    }
    try {
      const res = await fetch('/api/v1/pms/rate-plans', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) return;
      const plans = Array.isArray(data)
        ? data.filter((rp) => rp.is_active !== false).map((rp) => ({
          rate_plan_id: String(rp.rate_plan_id),
          rate_plan_code: String(rp.rate_plan_code || ''),
          rate_plan_name: String(rp.rate_plan_name || '')
        }))
        : [];
      setRatePlans(plans);
      if (selectedPlanId !== 'ALL' && !plans.some((rp) => String(rp.rate_plan_id) === String(selectedPlanId))) {
        setSelectedPlanId('ALL');
      }
    } catch (_) {
      setRatePlans([]);
      setSelectedPlanId('ALL');
    }
  };

  const load = async () => {
    if (!selectedProperty?.id) {
      setRows([]);
      setRatePlans([]);
      setValues({});
      setWeekStart('');
      setWeekEnd('');
      return;
    }
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const q = new URLSearchParams();
      if (selectedPlanId !== 'ALL') q.set('rate_plan_id', String(selectedPlanId));
      if (weekCursor) q.set('week_start', String(weekCursor));
      const res = await fetch(`/api/v1/pms/rate-matrix/weekly?${q.toString()}`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load rate matrix');
      const matrixRows = Array.isArray(data?.rows) ? data.rows : [];
      setRows(matrixRows);
      setWeekStart(String(data?.week_start || ''));
      setWeekEnd(String(data?.week_end || ''));

      const nextValues = {};
      matrixRows.forEach((row) => {
        OCC_COLS.forEach((col) => {
          const cellValue = row[col.key];
          nextValues[cellKey(row, col.key)] = cellValue == null || cellValue === '' ? null : Number(cellValue);
        });
      });
      setValues(nextValues);
    } catch (err) {
      setError(err?.message || 'Failed to load rate matrix');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id, selectedPlanId, weekCursor]);

  useEffect(() => {
    loadRatePlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const codeA = String(a.rate_plan_code || '');
      const codeB = String(b.rate_plan_code || '');
      if (codeA !== codeB) return codeA.localeCompare(codeB);
      return String(a.rate_date).localeCompare(String(b.rate_date));
    });
  }, [rows]);

  const dateColumns = useMemo(() => {
    const set = new Set(sortedRows.map((r) => String(r.rate_date || '')).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sortedRows]);

  const groupedPlans = useMemo(() => {
    const map = new Map();
    sortedRows.forEach((row) => {
      const planId = String(row.rate_plan_id);
      if (!map.has(planId)) {
        map.set(planId, {
          rate_plan_id: planId,
          rate_plan_code: row.rate_plan_code,
          rate_plan_name: row.rate_plan_name,
          meal_plan: row.meal_plan,
          daysByDate: new Map()
        });
      }
      map.get(planId).daysByDate.set(String(row.rate_date), row);
    });
    return Array.from(map.values());
  }, [sortedRows]);

  const goPreviousWeek = () => {
    if (!weekStart) return;
    setWeekCursor(shiftDateByDays(weekStart, -7));
  };

  const goNextWeek = () => {
    if (!weekStart) return;
    setWeekCursor(shiftDateByDays(weekStart, 7));
  };

  const goThisWeek = () => {
    setWeekCursor(getCurrentMonday());
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Front Office - Rate Matrix</h2>
      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.45rem' }}>
        <button onClick={onOpenUpdateRate} disabled={busy} className="btn-primary" style={{ border: '1px solid var(--theme-primary)', borderRadius: '4px', padding: '0.35rem 0.7rem', fontWeight: 600 }}>
          UPDATE RATE MATRIX
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        <strong>Filters</strong>
        <select value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)}>
          <option value="ALL">ALL RATE PLAN</option>
          {ratePlans.map((rp) => (
            <option key={rp.rate_plan_id} value={rp.rate_plan_id}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>
          ))}
        </select>
        <button onClick={goPreviousWeek} disabled={busy || !weekStart}>Previous 7 Days</button>
        <button onClick={goThisWeek} disabled={busy}>This Week</button>
        <button onClick={goNextWeek} disabled={busy || !weekStart}>Next 7 Days</button>
        <button onClick={load} disabled={busy}>Submit</button>
        <span style={{ color: '#4a5568', fontSize: '0.85rem' }}>
          Week starts on Monday: {weekStart ? fmtDate(weekStart) : '-'}
          {' | '}Week End: {weekEnd ? fmtDate(weekEnd) : '-'}
        </span>
      </div>

      {!groupedPlans.length ? (
        <div className="card" style={{ padding: '0.75rem', color: '#718096' }}>
          {busy ? 'Loading...' : 'No rows found'}
        </div>
      ) : groupedPlans.map((plan) => (
        <div key={plan.rate_plan_id} className="card" style={{ marginBottom: '0.8rem', overflowX: 'auto' }}>
          <div style={{ padding: '0.5rem 0.65rem', borderBottom: '1px solid var(--theme-border)', background: 'var(--theme-bg-light)', color: '#2d3748', fontSize: '0.86rem' }}>
            <strong>{plan.rate_plan_code}</strong> - {plan.rate_plan_name} | Meal Plan: {plan.meal_plan || 'EP'}
          </div>
          <table style={{ width: '100%', minWidth: `${Math.max(760, 180 + (dateColumns.length * 130))}px`, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--theme-bg-light)' }}>
                <th style={{ border: '1px solid #d9e2ec', padding: '0.45rem', textAlign: 'left', minWidth: '160px' }}>Occupancy</th>
                {dateColumns.map((dateOnly) => (
                  <th key={`${plan.rate_plan_id}-${dateOnly}`} style={{ border: '1px solid #d9e2ec', padding: '0.45rem', textAlign: 'right', minWidth: '120px' }}>
                    {fmtDate(dateOnly)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {OCC_COLS.map((col) => (
                <tr key={`${plan.rate_plan_id}-${col.key}`}>
                  <td style={{ border: '1px solid var(--theme-border)', padding: '0.4rem', fontWeight: 600, textTransform: 'capitalize' }}>{col.label}</td>
                  {dateColumns.map((dateOnly) => {
                    const dayRow = plan.daysByDate.get(dateOnly);
                    const isLocked = !dayRow || !dayRow.editable;
                    const lockReason = dayRow && isLocked ? getLockReason(dayRow) : '';
                    const key = dayRow ? cellKey(dayRow, col.key) : `${plan.rate_plan_id}|${dateOnly}|${col.key}`;
                    return (
                      <td key={`${plan.rate_plan_id}-${col.key}-${dateOnly}`} style={{ border: '1px solid var(--theme-border)', padding: '0.25rem', background: isLocked ? 'var(--theme-border-light)' : '#fff', position: 'relative' }}>
                        {isLocked ? (
                          <span
                            title={lockReason}
                            aria-label={lockReason}
                            style={{ position: 'absolute', top: '2px', left: '4px', fontSize: '0.62rem', color: '#718096', lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}
                          >
                            🔒
                          </span>
                        ) : null}
                        <span
                          title={isLocked ? lockReason : ''}
                          style={{ display: 'block', width: '100%', textAlign: 'right', border: '1px solid #cbd5e0', borderRadius: '4px', padding: '0.25rem 0.35rem', background: isLocked ? '#edf2f7' : '#fff', color: busy ? '#718096' : '#1a202c' }}
                        >
                          {formatRateLabel(values[key])}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div style={{ marginTop: '0.45rem', color: '#4a5568', fontSize: '0.82rem' }}>
        View only: Edit Rate Matrix will be available in a separate screen. Hover locked cells to see plan window lock reason.
      </div>
    </div>
  );
}

export default PmsRateMatrix;
